import type { UserApplication } from "../domain/types";
import { DEMO_PROFILES } from "./demoPool";
import {
  getIneligibilityReasons,
  isEligibleForMatching,
  matchProfileFromApplication,
} from "./eligibility";
import type { MatchingPoolEntry, MatchProfile } from "./types";

const POOL_KEY = "un8.matching.pool.v1";

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function readPool(): Record<string, MatchingPoolEntry> {
  try {
    const raw = localStorage.getItem(POOL_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, MatchingPoolEntry>;
  } catch {
    return {};
  }
}

function writePool(map: Record<string, MatchingPoolEntry>) {
  try {
    localStorage.setItem(POOL_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode — last write may fail; session still has in-memory map if we keep one */
  }
  notify();
}

export type UpsertPoolResult =
  | { ok: true; entry: MatchingPoolEntry; created: boolean }
  | { ok: false; reasons: string[] };

/**
 * Persistent matching pool for the local prototype.
 * Keyed by applicationId so approve never duplicates and never drops other members.
 *
 * Later: POST /api/matching-pool on approve.
 */
export const matchingPoolStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** All pool profiles (order: newest updated first). */
  listProfiles(): MatchProfile[] {
    return this.listEntries().map((e) => e.profile);
  },

  listEntries(): MatchingPoolEntry[] {
    return Object.values(readPool()).sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt)
    );
  },

  get(applicationId: string): MatchingPoolEntry | null {
    return readPool()[applicationId] ?? null;
  },

  getProfile(applicationId: string): MatchProfile | null {
    return this.get(applicationId)?.profile ?? null;
  },

  has(applicationId: string): boolean {
    return Boolean(readPool()[applicationId]);
  },

  size(): number {
    return Object.keys(readPool()).length;
  },

  /** Direct profile upsert (e.g. migrate legacy rows). Does not re-check eligibility. */
  upsertProfile(profile: MatchProfile, applicationId?: string): MatchingPoolEntry {
    const map = readPool();
    const id = applicationId ?? profile.id;
    const prev = map[id];
    const now = new Date().toISOString();
    const entry: MatchingPoolEntry = {
      applicationId: id,
      profile: {
        ...profile,
        id,
        source: "pool",
        joinedAt: profile.joinedAt ?? prev?.enteredAt ?? now,
      },
      enteredAt: prev?.enteredAt ?? now,
      updatedAt: now,
    };
    map[id] = entry;
    writePool(map);
    return entry;
  },

  /**
   * Upsert from a UserApplication after approve.
   * Only writes when isEligibleForMatching is true.
   * Existing entries for other applications are never removed.
   */
  upsertFromApplication(app: UserApplication): UpsertPoolResult {
    if (!isEligibleForMatching(app)) {
      return { ok: false, reasons: getIneligibilityReasons(app) };
    }
    const profile = matchProfileFromApplication(app);
    if (!profile) {
      return { ok: false, reasons: getIneligibilityReasons(app) };
    }

    const map = readPool();
    const userId = app.userId || app.id;
    const prev = map[userId];
    const now = new Date().toISOString();
    const entry: MatchingPoolEntry = {
      applicationId: userId,
      profile: {
        ...profile,
        id: userId,
        source: "pool",
        joinedAt: profile.joinedAt ?? prev?.enteredAt ?? now,
      },
      enteredAt: prev?.enteredAt ?? now,
      updatedAt: now,
    };
    map[userId] = entry;
    writePool(map);
    return { ok: true, entry, created: !prev };
  },

  /** Remove one member (reject / resubmit / ban). Does not touch others. */
  remove(applicationId: string): boolean {
    const map = readPool();
    if (!map[applicationId]) return false;
    delete map[applicationId];
    writePool(map);
    return true;
  },

  /**
   * Rebuild/upsert from a full application list without losing unrelated pool members
   * that are still approved+eligible. Removes pool rows whose applications are no longer
   * approved/eligible when `pruneMissing` is true.
   */
  syncFromApplications(
    apps: UserApplication[],
    options?: { pruneMissing?: boolean }
  ): { upserted: number; removed: number; skipped: number } {
    const map = readPool();
    let upserted = 0;
    let skipped = 0;
    const keep = new Set<string>();

    for (const app of apps) {
      if (!isEligibleForMatching(app)) {
        skipped += 1;
        if (options?.pruneMissing && map[app.id]) {
          delete map[app.id];
        }
        continue;
      }
      const profile = matchProfileFromApplication(app);
      if (!profile) {
        skipped += 1;
        continue;
      }
      keep.add(app.id);
      const prev = map[app.id];
      const now = new Date().toISOString();
      map[app.id] = {
        applicationId: app.id,
        profile: { ...profile, source: "pool" },
        enteredAt: prev?.enteredAt ?? now,
        updatedAt: now,
      };
      upserted += 1;
    }

    let removed = 0;
    if (options?.pruneMissing) {
      for (const id of Object.keys(map)) {
        if (!keep.has(id)) {
          delete map[id];
          removed += 1;
        }
      }
    }

    writePool(map);
    return { upserted, removed, skipped };
  },

  clear(): void {
    writePool({});
  },

  /**
   * Seed DEMO_PROFILES into the live matching pool (clearly source: demo).
   * Idempotent — does not overwrite existing rows. Never used as a real member pool.
   */
  seedDemoProfiles(): number {
    const map = readPool();
    let added = 0;
    const now = new Date().toISOString();
    for (const profile of DEMO_PROFILES) {
      if (map[profile.id]) continue;
      map[profile.id] = {
        applicationId: profile.id,
        profile: { ...profile, source: "demo" },
        enteredAt: now,
        updatedAt: now,
      };
      added += 1;
    }
    if (added > 0) writePool(map);
    return added;
  },
};

/**
 * Apply a verification decision to the matching pool.
 * Call after the application record has been updated.
 */
export function applyReviewDecisionToPool(
  app: UserApplication,
  status: UserApplication["verification"]["status"]
): UpsertPoolResult | { ok: true; removed: boolean } {
  if (status === "approved") {
    return matchingPoolStore.upsertFromApplication(app);
  }
  // Reject / resubmit / draft — leave pool so no non-approved stay matchable
  const removed = matchingPoolStore.remove(app.id);
  return { ok: true, removed };
}
